/**
 * 🎯 Catálogo GLOBAL vía REST público (`/api/catalog/*`) — guest-first.
 *
 * El catálogo es dato compartido [H] sin dueño. El invitado (sin token de sync)
 * lo ojea por estos endpoints REST; el usuario autenticado también puede usarlos
 * (el router es público) además de tener el dataset cacheado por el stream
 * `catalog_public`. Las respuestas del servidor son camelCase (serde
 * `rename_all = "camelCase"`), a diferencia de las filas locales de PowerSync
 * (snake_case). Este módulo es la ÚNICA fuente de las pantallas de catálogo, así
 * que la app funciona idéntica con o sin cuenta.
 */

import { SERVER_BASE_URL } from "@/lib/api";

export interface CatalogProductDTO {
  id: string;
  ean?: string | null;
  upc?: string | null;
  editionName: string;
  releaseYear?: number | null;
  category?: string | null;
  seriesId?: string | null;
  imageUrl?: string | null;
  description?: string | null;
  isLimited: boolean;
}

interface CatalogPartDTO {
  id: string;
  productId: string;
  name: string;
  kind?: string | null;
  imageUrl?: string | null;
  sortOrder: number;
}

function catalogUrl(path: string): string {
  return `${SERVER_BASE_URL.replace(/\/$/, "")}/api/catalog${path}`;
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(catalogUrl(path), { credentials: "include", signal });
  if (!res.ok) {
    throw new Error(`catalog REST ${path} → HTTP ${res.status}`);
  }
  // Frontera JSON: la forma del DTO la garantiza el contrato REST del servidor
  // (utoipa camelCase). No hay validador en runtime aquí; es una aserción de
  // borde deliberada, no una conversión insegura desde `any`.
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return res.json() as Promise<T>;
}

/** Lista de productos del catálogo, opcionalmente filtrada por categoría. */
export async function fetchCatalogProducts(opts?: {
  category?: string;
  signal?: AbortSignal;
}): Promise<CatalogProductDTO[]> {
  // eslint-disable-next-line lingui/no-unlocalized-strings -- URL query param, no user-facing text.
  const qs = opts?.category ? `?category=${encodeURIComponent(opts.category)}` : "";
  return getJson<CatalogProductDTO[]>(`/products${qs}`, opts?.signal);
}

/** Detalle de un producto por id. Devuelve `null` si no existe (404). */
export async function fetchCatalogProduct(
  id: string,
  signal?: AbortSignal,
): Promise<CatalogProductDTO | null> {
  try {
    return await getJson<CatalogProductDTO>(`/product/${encodeURIComponent(id)}`, signal);
  } catch {
    return null;
  }
}

/** Piezas de un producto. */
export async function fetchProductParts(
  id: string,
  signal?: AbortSignal,
): Promise<CatalogPartDTO[]> {
  return getJson<CatalogPartDTO[]>(`/product/${encodeURIComponent(id)}/parts`, signal);
}

/**
 * 🎯 Resuelve un producto por código de barras (EAN/UPC). Corazón del escáner.
 * Devuelve `null` si el código no está en el catálogo (404).
 */
export async function resolveBarcode(
  code: string,
  signal?: AbortSignal,
): Promise<CatalogProductDTO | null> {
  try {
    return await getJson<CatalogProductDTO>(`/resolve/${encodeURIComponent(code)}`, signal);
  } catch {
    return null;
  }
}
